// ============================================================================
// REPORT PRINT PREVIEW (FSA-1, post-refactor)
// ============================================================================
// Renders a financial-statement report (Balance Sheet / P&L / Cash Flow)
// from the report's `layout` JSON (added in fix 022 + 023).
//
// Columns:
//   Particulars                          Note    As at <CY>    As at <PY>
//
// Amounts are blank ("—") because FSA-1 ships templates only. The
// compositor (FSA-2.2) replaces "—" with classified-ledger totals,
// engagement note data values, and formula results.
//
// Used for reportType = 'FINANCIAL_STATEMENT' only. Notes drilldown uses
// NoteDetailView, narratives use NarrativePreview.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type {
  TemplateReportNode,
  LayoutLine,
} from 'api/types/fsa/template.types';
import { parseLayout } from 'api/types/fsa/template.types';

interface ReportPrintPreviewProps {
  reportNode: TemplateReportNode;
  /** Click handler for a NOTE_TOTAL line (passes schedule code → drill into note). */
  onOpenNote?: (linkedScheduleCode: string) => void;
}

const ReportPrintPreview: React.FC<ReportPrintPreviewProps> = ({ reportNode, onOpenNote }) => {
  const { report } = reportNode;
  const layout = parseLayout(report);

  if (!layout || !layout.lines || layout.lines.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={emptyStateStyle}>
          <IconSystem.alert size={18} style={{ marginRight: 6 }} />
          No layout configured for this report. The compositor renders FS reports
          from <code>tbl_template_report.layout</code> JSON (added in fix 022 + 023).
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <div style={reportTitleBlockStyle}>
          <div style={reportSubtitleStyle}>FOR THE YEAR ENDED 31 MARCH</div>
          <h2 style={reportTitleStyle}>{report.name}</h2>
        </div>
        <div style={amountsLegendStyle}>
          <IconSystem.info size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          Template preview. Note numbers and amounts are assigned per engagement —
          lines without data will show "—" with no note number on the printed statement.
        </div>
      </header>

      <div style={tableWrapperStyle}>
        <table style={printTableStyle}>
          <colgroup>
            <col />
            <col style={{ width: 200 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 140 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left' }}>Particulars</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Linked Note</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>
                As at <br />
                <span style={periodLabelStyle}>Current Year</span>
              </th>
              <th style={{ ...thStyle, textAlign: 'right' }}>
                As at <br />
                <span style={periodLabelStyle}>Previous Year</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {layout.lines.map((line, idx) => (
              <LineRow
                key={`${line.code ?? 'header'}-${idx}`}
                line={line}
                onOpenNote={onOpenNote}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportPrintPreview;

// ============================================================================
// LINE ROW — dispatches by line.kind
// ============================================================================

const LineRow: React.FC<{
  line: LayoutLine;
  onOpenNote?: (linkedScheduleCode: string) => void;
}> = ({ line, onOpenNote }) => {
  const indent = Math.min(line.indent ?? 0, 3);
  const indentPx = 12 + indent * 16;
  const kind = line.kind;

  switch (kind) {
    case 'SECTION_HEADER': {
      // Primary bands (indent 0/1) print bold + banded — they're real
      // section dividers ("I. EQUITY AND LIABILITIES", "(1) Shareholders'
      // Funds"). Sub-grouping headers (indent ≥ 2 — e.g. "(b) Trade Payables"
      // wrapping its (A)/(B) sub-items) print in normal weight so they
      // sit inline with the surrounding (a)/(c)/(d) NOTE_TOTAL lines.
      // Explicit `is_bold` on the line overrides this default either way.
      const defaultBold = indent < 2;
      const sectionBold = line.is_bold ?? defaultBold;
      const isPrimary = indent === 0;
      return (
        <tr>
          <td colSpan={4} style={{
            ...sectionHeaderRowStyle,
            paddingLeft: indentPx,
            fontWeight: sectionBold ? 700 : 400,
            fontSize: isPrimary ? '0.85rem' : '0.78rem',
            textTransform: isPrimary ? 'uppercase' : 'none',
            background: isPrimary
              ? '#dbeafe'
              : indent === 1
                ? 'var(--bg-secondary)'
                : 'transparent',
            color: isPrimary ? '#1e3a8a' : 'var(--text-primary)',
            borderTop: isPrimary
              ? '2px solid #2563eb'
              : indent === 1
                ? '1px solid var(--border-color)'
                : '1px dashed var(--border-color)',
            borderBottom: isPrimary
              ? '2px solid #2563eb'
              : indent === 1
                ? '1px solid var(--border-color)'
                : '1px dashed var(--border-color)',
          }}>
            {line.label}
          </td>
        </tr>
      );
    }

    case 'NOTE_TOTAL': {
      const noteCode = line.note_schedule_code;
      const classCode = line.note_class_code;
      return (
        <tr>
          <td style={{ ...tdLabelStyle, paddingLeft: indentPx, fontWeight: line.is_bold ? 700 : 400 }}>
            {line.label}
          </td>
          <td style={tdNoteStyle}>
            {noteCode && onOpenNote ? (
              <button
                onClick={() => onOpenNote(noteCode)}
                style={noteLinkStyle}
                title={classCode ? `Open ${noteCode} → ${classCode}` : `Open ${noteCode}`}
              >
                {noteCode}
                {classCode && <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}> · {classCode}</span>}
              </button>
            ) : noteCode ? (
              <span>{noteCode}{classCode && ` · ${classCode}`}</span>
            ) : (
              ''
            )}
          </td>
          <td style={{ ...tdAmountStyle, fontWeight: line.is_bold ? 700 : 400 }}>—</td>
          <td style={{ ...tdAmountStyle, fontWeight: line.is_bold ? 700 : 400 }}>—</td>
        </tr>
      );
    }

    case 'VALUE':
      return (
        <tr>
          <td style={{ ...tdLabelStyle, paddingLeft: indentPx, fontWeight: line.is_bold ? 700 : 400 }}>
            {line.label}
          </td>
          <td style={tdNoteStyle}>
            <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.7rem' }}>
              manual entry
            </span>
          </td>
          <td style={{ ...tdAmountStyle, fontWeight: line.is_bold ? 700 : 400 }}>____</td>
          <td style={{ ...tdAmountStyle, fontWeight: line.is_bold ? 700 : 400 }}>____</td>
        </tr>
      );

    case 'SUBTOTAL':
    case 'TOTAL': {
      const isGrand = kind === 'TOTAL' || indent === 0;
      const labelBg = isGrand ? '#dbeafe' : 'var(--bg-secondary)';
      return (
        <tr style={{ background: labelBg }}>
          <td style={{
            ...tdLabelStyle,
            paddingLeft: indentPx,
            fontWeight: 700,
            borderTop: isGrand ? '2px solid var(--text-primary)' : '1px solid var(--text-primary)',
            textTransform: isGrand ? 'uppercase' : 'none',
          }}
            title={line.formula ? `Σ ${line.formula}` : undefined}
          >
            {line.label}
          </td>
          <td style={{ ...tdNoteStyle, borderTop: isGrand ? '2px solid var(--text-primary)' : '1px solid var(--text-primary)' }}>
            {line.formula && (
              <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.7rem' }}>
                Σ formula
              </span>
            )}
          </td>
          <td style={{ ...tdAmountStyle, fontWeight: 700, borderTop: isGrand ? '2px solid var(--text-primary)' : '1px solid var(--text-primary)' }}>—</td>
          <td style={{ ...tdAmountStyle, fontWeight: 700, borderTop: isGrand ? '2px solid var(--text-primary)' : '1px solid var(--text-primary)' }}>—</td>
        </tr>
      );
    }

    default:
      // Forward-compat for any future line kinds.
      return (
        <tr>
          <td colSpan={4} style={{ ...tdLabelStyle, paddingLeft: indentPx, color: 'var(--text-secondary)' }}>
            {line.label ?? `(unknown line kind: ${kind})`}
          </td>
        </tr>
      );
  }
};

// ============================================================================
// STYLES
// ============================================================================

const containerStyle: React.CSSProperties = { padding: '0.5rem 0' };

const headerStyle: React.CSSProperties = {
  marginBottom: '0.75rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: '1rem',
  flexWrap: 'wrap',
};

const reportTitleBlockStyle: React.CSSProperties = { flex: 1 };
const reportSubtitleStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 1,
};
const reportTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.1rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
};

const amountsLegendStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  maxWidth: 320,
  textAlign: 'right',
};

const tableWrapperStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  overflow: 'hidden',
};

const printTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.8125rem',
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
};

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderBottom: '2px solid var(--text-primary)',
  fontWeight: 700,
  fontSize: '0.75rem',
  background: 'var(--bg-secondary)',
};

const periodLabelStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 500,
  color: 'var(--text-secondary)',
};

const tdLabelStyle: React.CSSProperties = {
  padding: '0.35rem 0.75rem',
  borderBottom: '1px dashed var(--border-color)',
};

const tdNoteStyle: React.CSSProperties = {
  padding: '0.35rem 0.5rem',
  borderBottom: '1px dashed var(--border-color)',
  textAlign: 'left',
  fontSize: '0.75rem',
};

const tdAmountStyle: React.CSSProperties = {
  padding: '0.35rem 0.75rem',
  borderBottom: '1px dashed var(--border-color)',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

const noteLinkStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: '0 4px',
  color: '#2563eb',
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 600,
  textDecoration: 'underline',
  textAlign: 'left',
};

const sectionHeaderRowStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  fontWeight: 700,
};

const emptyStateStyle: React.CSSProperties = {
  padding: '1.5rem',
  textAlign: 'center',
  color: 'var(--text-secondary)',
  background: 'var(--bg-secondary)',
  border: '1px dashed var(--border-color)',
  borderRadius: 6,
  fontSize: '0.85rem',
};
